import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Info } from "lucide-react";

/**
 * LegalPage — shared wrapper for all German legal pages (AGB, Impressum,
 * Datenschutz, Widerruf, Widerrufsformular).
 *
 * ⚠️ NON-PUBLIC NOTICE:
 * Legal pages contain placeholders and must not be considered final until
 * operator data is inserted and reviewed. See placeholders such as
 * [BETREIBER_NAME], [GESCHÄFTSADRESSE], [E-MAIL], [STAND_DATUM].
 *
 * The content of these pages is kept German-only for legal validity in DE.
 * For non-German visitors, we display a localized notice at the top
 * clarifying that the German version is the binding one.
 */
export default function LegalPage({ title, subtitle, updatedAt, children }) {
  const { t, i18n } = useTranslation();
  const currentLang = (i18n.resolvedLanguage || i18n.language || "en").split("-")[0];
  const showNonDeNotice = currentLang !== "de";

  return (
    <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12 py-16 sm:py-20">
      <div className="mb-10">
        <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-3">
          <Link to="/" className="hover:text-white transition-colors">PineForge.AI</Link>
          <span className="mx-2 text-zinc-700">/</span>
          <span>Rechtliches</span>
        </p>
        <h1
          className="text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-semibold mb-3"
          data-testid="legal-page-title"
        >
          {title}
        </h1>
        {subtitle && (
          <p className="text-zinc-400 leading-relaxed">{subtitle}</p>
        )}
        {updatedAt && (
          <p className="mt-3 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
            Stand: {updatedAt}
          </p>
        )}
      </div>

      {showNonDeNotice && (
        <div
          data-testid="legal-de-binding-notice"
          className="mb-8 flex items-start gap-3 text-sm text-blue-100 bg-blue-500/10 border border-blue-500/30 rounded-lg px-4 py-3"
        >
          <Info className="w-4 h-4 text-blue-300 mt-0.5 flex-shrink-0" />
          <span>{t("legal.review_notice")}</span>
        </div>
      )}

      <article
        data-testid="legal-page-content"
        className="legal-prose text-sm sm:text-[15px] text-zinc-300 leading-relaxed space-y-8"
      >
        {children}
      </article>
    </div>
  );
}
